import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Route as RouteIcon, CalendarClock, UserRound, Building2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PageLoader } from '@shared/components/PageLoader';
import { journeyAPI } from '@/utils/supabase/client';
import {
  JOURNEY_PHASES,
  journeyDisplayName,
  type JourneyProspect,
} from '@/types/journey';
import { JourneyStageBadge } from './JourneyStageBadge';
import { cn } from '@/components/ui/utils';

function formatMeeting(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function JourneyCard({ journey, onClick }: { journey: JourneyProspect; onClick: () => void }) {
  const meeting = formatMeeting(journey.meeting_start_at);
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg border bg-card p-3 hover:border-foreground/30 hover:shadow-sm transition-all"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium leading-tight">{journeyDisplayName(journey)}</span>
          {journey.client_id && (
            <span
              className="shrink-0 text-[10px] uppercase tracking-wide font-semibold text-success bg-success/15 rounded px-1.5 py-0.5"
              title="Linked to a CRM client"
            >
              Client
            </span>
          )}
        </div>

        {journey.company_name && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{journey.company_name}</span>
          </span>
        )}

        <JourneyStageBadge stage={journey.stage} className="self-start" />

        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          {journey.connector_email && (
            <span className="flex items-center gap-1" title={`Introduced by ${journey.connector_email}`}>
              <UserRound className="h-3 w-3 shrink-0" />
              <span className="truncate">{journey.connector_email}</span>
            </span>
          )}
          {meeting && (
            <span className="flex items-center gap-1">
              <CalendarClock className="h-3 w-3 shrink-0" />
              {meeting}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export function JourneyBoard() {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState<JourneyProspect[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const didInit = useRef(false);
  const [search, setSearch] = useState('');

  const load = useCallback((q?: string) => {
    // Only the first load swaps the list out for <PageLoader />. Blanking the
    // list on every debounced search keystroke throws away the scroll position
    // and flashes the whole page.
    if (!loadedOnce.current) setLoading(true);
    journeyAPI
      .getAll({ q: q && q.length >= 2 ? q : undefined })
      .then(({ journeys: j, total: t }) => {
        setJourneys(j);
        setTotal(t);
      })
      .catch(console.error)
      .finally(() => { loadedOnce.current = true; setLoading(false); });
  }, []);

  useEffect(() => {
    // One effect owns loading. The first run fires immediately so the page isn't
    // blank for the debounce interval; every run after that debounces keystrokes.
    if (!didInit.current) {
      didInit.current = true;
      load(search);
      return;
    }
    const timer = setTimeout(() => load(search), 300);
    return () => clearTimeout(timer);
  }, [search, load]);

  const byPhase = useMemo(() => {
    return JOURNEY_PHASES.map((phase) => ({
      phase,
      items: journeys.filter((j) => phase.stages.includes(j.stage)),
    }));
  }, [journeys]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="border-b px-4 lg:px-6 py-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search person, company, connector…"
            className="pl-9"
          />
        </div>
        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
          {loading ? '…' : `${total} ${total === 1 ? 'journey' : 'journeys'}`}
        </span>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <PageLoader />
        ) : journeys.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <RouteIcon className="h-10 w-10 opacity-30" />
            <p className="text-sm">No journeys found</p>
            <p className="text-xs max-w-sm text-center">
              Journeys are created automatically when an intro email is detected.
            </p>
          </div>
        ) : (
          <div className="flex gap-4 p-4 lg:p-6 min-w-max h-full items-start">
            {byPhase.map(({ phase, items }) => (
              <div key={phase.key} className="flex flex-col gap-3 w-64 shrink-0">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{phase.label}</span>
                    <span
                      className={cn(
                        'text-xs tabular-nums rounded-full px-1.5 py-0.5',
                        items.length > 0
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      {items.length}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground leading-snug">{phase.description}</span>
                </div>

                <div className="flex flex-col gap-2">
                  {items.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                      Empty
                    </div>
                  ) : (
                    items.map((j) => (
                      <JourneyCard
                        key={j.id}
                        journey={j}
                        onClick={() => navigate(`/journey/${j.id}`)}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
